// Admin quality-issues triage — API client.
// Backend: app/routes/quality_issues_admin.py (/api/admin/quality-issues).

import api from './api';

export type QualityIssueStatus =
  | 'open'
  | 'in_review'
  | 'resolved'
  | 'closed'
  | 'duplicate';
export type QualityIssueSeverity = 'low' | 'medium' | 'high' | 'critical';
export type QualityIssueCategory =
  | 'packaging'
  | 'quality'
  | 'shipping'
  | 'billing'
  | 'application'
  | 'other';
export type QualityIssueSource =
  | 'qcp'
  | 'email'
  | 'phone'
  | 'admin'
  | 'legacy_packaging';
export type QualityIssueEventTrigger = 'user' | 'agent' | 'admin' | 'system';

export const STATUS_DISPLAY: Record<QualityIssueStatus, string> = {
  open: 'Open',
  in_review: 'In review',
  resolved: 'Resolved',
  closed: 'Closed',
  duplicate: 'Duplicate',
};

export const SEVERITY_DISPLAY: Record<QualityIssueSeverity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

export interface QualityIssue {
  id: string;
  issue_no: string;
  status: QualityIssueStatus;
  severity: QualityIssueSeverity;
  category: QualityIssueCategory;
  source: QualityIssueSource;
  reporter_internal_user_id: string | null;
  acting_for_customer_id: string | null;
  customer_contact_id: string | null;
  related_order_id: string | null;
  related_sage_so_no: string | null;
  product_skus: Record<string, unknown>[];
  occurred_at: string | null;
  jobsite: string | null;
  csr_contact: string | null;
  title: string;
  description: string;
  business_impact: string | null;
  tags: string[];
  attachments: Record<string, unknown>[];
  assistant_conversation_id: string | null;
  audit_trail: Record<string, unknown> | null;
  resolution: string | null;
  resolution_owner_id: string | null;
  resolved_at: string | null;
  duplicate_of_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface QualityIssueEvent {
  id: string;
  issue_id: string;
  from_status: QualityIssueStatus | null;
  to_status: QualityIssueStatus;
  trigger: QualityIssueEventTrigger;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
  note: string | null;
  created_at: string;
}

export interface QualityIssueListResponse {
  environment: string;
  items: QualityIssue[];
  total: number;
  limit: number;
  offset: number;
}

export interface IssueDetailResponse {
  environment: string;
  issue: QualityIssue;
  events: QualityIssueEvent[];
  // Statuses an admin can drive from the issue's current status.
  allowed_transitions: QualityIssueStatus[];
}

export interface ListParams {
  status?: QualityIssueStatus;
  severity?: QualityIssueSeverity;
  category?: QualityIssueCategory;
  source?: QualityIssueSource;
  q?: string;
  // Exact-match entity filters (UUIDs).
  customer_id?: string;
  reporter_id?: string;
  // Inclusive calendar-day window (YYYY-MM-DD, UTC) over created_at.
  created_from?: string;
  created_to?: string;
  limit?: number;
  offset?: number;
}

// trigger + actor are forced server-side; the client never sends them.
export interface TransitionPayload {
  to_status: QualityIssueStatus;
  note?: string | null;
  resolution?: string | null;
  resolution_owner_id?: string | null;
  duplicate_of_id?: string | null;
}

const BASE = '/admin/quality-issues';

export const qualityIssuesApi = {
  list: async (params?: ListParams): Promise<QualityIssueListResponse> => {
    const r = await api.get<QualityIssueListResponse>(BASE, { params });
    return r.data;
  },

  get: async (id: string): Promise<IssueDetailResponse> => {
    const r = await api.get<IssueDetailResponse>(`${BASE}/${id}`);
    return r.data;
  },

  transition: async (
    id: string,
    payload: TransitionPayload,
  ): Promise<IssueDetailResponse> => {
    const r = await api.post<IssueDetailResponse>(
      `${BASE}/${id}/transition`,
      payload,
    );
    return r.data;
  },
};

// Deep links out of the triage page. The QCP door's chat surface lives at
// /portal/quality-copilot/ask and consumes ?conversation=<id> on mount;
// the portal orders page opens the full modal via ?orderId=<uuid>.
export function conversationHref(conversationId: string): string {
  return `/portal/quality-copilot/ask?conversation=${encodeURIComponent(conversationId)}`;
}

export function orderHref(orderId: string): string {
  return `/portal/orders?orderId=${encodeURIComponent(orderId)}`;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Entity filters are only sent once they parse as a UUID — a half-pasted id
// would otherwise 422 on every keystroke.
export function isUuid(v: string): boolean {
  return UUID_RE.test(v);
}
